import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ICON } from '../icons/iconIds';
import SelkirkIcon from './icons/SelkirkIcon';
import IconText from './icons/IconText';
import Layout from './layout/Layout';
import Card from './ui/Card';
import Button from './ui/Button';
import Spinner from './ui/Spinner';
import SampleLocationMap from './SampleLocationMap';
import { authService } from '../auth/authService';
import { getAuthorizationService } from '../services/authorizationService';
import { sampleTrackingService } from '../services/sampleTrackingService';
import { drillingService } from '../services/drillingService';
import { pickLatestProjectFromSources } from '../utils/drillingCampaignHelpers';
import { navigateToQaqcFailure } from '../utils/qaqcDeepLink';
import { COLMATCH_STATUS } from '../utils/sampleTrackingColmatchStatus';
import { exportToCSV } from '../utils/csvExport';
import {
  sourceTooltip,
  SAMPLE_TRACKING_DATA_SOURCES,
} from '../utils/sampleTrackingDataSources';
import {
  groupWorkorderDetailsByType,
  isQaqcRelevantWorkorderType,
  WORKORDER_TYPE_DISPLAY_ORDER,
} from '../utils/sampleTrackingWorkorderTypes';
import './SampleTracking.css';

/** Flatten nested values for CSV (arrays, objects, Date) */
function serializeSampleTrackingField(value) {
  if (value === null || value === undefined) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '';
    if (typeof value[0] === 'object' && value[0] !== null) {
      return JSON.stringify(value);
    }
    return value.map(String).join('; ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

/** Fixed column order for CSV export (hole.azi exported as azimuth) */
const SAMPLE_TRACKING_CSV_COLUMNS = [
  { header: 'hole_id', get: (h) => h.hole_id },
  { header: 'easting', get: (h) => h.easting },
  { header: 'northing', get: (h) => h.northing },
  { header: 'azimuth', get: (h) => h.azi },
  { header: 'dip', get: (h) => h.dip },
  { header: 'eoh', get: (h) => h.eoh },
  { header: 'expected_completion_date', get: (h) => h.expected_completion_date },
  { header: 'qaqc_passed_array', get: (h) => h.qaqc_passed_array },
  { header: 'news_release_date', get: (h) => h.news_release_date },
  { header: 'workorder_numbers_array', get: (h) => h.workorder_numbers_array },
  { header: 'workorder_statuses_array', get: (h) => h.workorder_statuses_array },
  { header: 'colmatch_passed_sample_count', get: (h) => h.colmatch_passed_sample_count },
  { header: 'colmatch_expected_sample_count', get: (h) => h.colmatch_expected_sample_count },
  { header: 'colmatch_hole_passed', get: (h) => h.colmatch_hole_passed },
  { header: 'colmatch_fail_reason', get: (h) => h.colmatch_fail_reason },
  { header: 'colmatch_display_label', get: (h) => h.colmatch_display_label },
];

const WORKORDER_TYPE_LABELS = {
  'STANDARD ANALYSIS': 'Standard Analysis',
  'STANDARD + SEQUENTIAL': 'Standard + Sequential',
  'SEQUENTIAL LEACH': 'Sequential Leach',
  'RECEPTION WO': 'Reception WO',
};

function formatCadCurrency(value) {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value));
}

const SampleTracking = ({ user }) => {
  const [trackingData, setTrackingData] = useState([]);
  const [summaryMetrics, setSummaryMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('hole_id');
  const [sortDirection, setSortDirection] = useState('asc');
  const [selectedHole, setSelectedHole] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [qaqcFilter, setQaqcFilter] = useState('all');
  const [newsReleaseFilter, setNewsReleaseFilter] = useState('not_released'); // Default to show unreleased holes only
  const [lastSync, setLastSync] = useState(null);
  const [lastSyncLoading, setLastSyncLoading] = useState(true);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [canAccessQaqc, setCanAccessQaqc] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [sampleLocationData, setSampleLocationData] = useState([]);
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline' or 'map'
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [costMetrics, setCostMetrics] = useState(null);
  const [costMetricsLoading, setCostMetricsLoading] = useState(false);
  const [costMetricsError, setCostMetricsError] = useState(null);
  const [projectSources, setProjectSources] = useState([]);
  const [sampleProgressProjects, setSampleProgressProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState('all');
  const initialCostCampaignTabRef = useRef(false);

  const sortIndicator = (field) =>
    sortField === field ? (
      <SelkirkIcon
        name={sortDirection === 'asc' ? ICON.uiSortAsc : ICON.uiSortDesc}
        size={12}
        decorative
      />
    ) : null;

  // Load data on component mount
  useEffect(() => {
    // Sync QAQC status first, then load data
    syncAndLoadData();
    loadLastSync();
    loadSampleLocationData();
    loadLabCostMetrics();
    loadCostCampaignSources();
  }, []);

  useEffect(() => {
    if (initialCostCampaignTabRef.current) return;
    if (!projectSources.length) return;
    initialCostCampaignTabRef.current = true;
    const latest = pickLatestProjectFromSources(projectSources);
    const id = latest?.project_id;
    if (id != null && String(id) !== '') {
      setSelectedProjectId(String(id));
    }
  }, [projectSources]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const authz = getAuthorizationService(authService);
        await authz.initializeUserPermissions();
        if (!cancelled) {
          setCanAccessQaqc(authz.hasAccessToApp('qaqc'));
        }
      } catch {
        if (!cancelled) setCanAccessQaqc(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const syncAndLoadData = async () => {
    try {
      setLoading(true);
      setError(null);
      setWarnings(null);
      
      // Step 1: Sync QAQC status (non-blocking, runs in background)
      console.log('🔄 Step 1: Syncing QAQC status...');
      const syncResult = await sampleTrackingService.syncQaqcStatus();
      if (syncResult.success) {
        console.log(`✅ Synced ${syncResult.stats?.updated || 0} workorders`);
      } else {
        console.warn('⚠️ QAQC sync had issues, continuing with data load');
      }
      
      // Step 2: Load dashboard data
      console.log('📊 Step 2: Loading dashboard data...');
      await loadDashboardData();
      
    } catch (err) {
      console.error('❌ Error in sync and load:', err);
      setError(err.message || 'Failed to load sample tracking data');
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      const result = await sampleTrackingService.getDashboardData();
      
      setTrackingData(result.data || []);
      setSummaryMetrics(result.summary || null);
      setWarnings(result.warnings || null);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error loading sample tracking data:', err);
      setError(err.message || 'Failed to load sample tracking data');
      setLoading(false);
    }
  };

  const loadLabCostMetrics = async () => {
    try {
      setCostMetricsLoading(true);
      setCostMetricsError(null);
      const data = await sampleTrackingService.getLabApprovalCostMetrics();
      setCostMetrics(data);
    } catch (err) {
      console.warn('Lab approval cost metrics unavailable:', err);
      setCostMetricsError(err.message || 'Failed to load lab approval cost metrics');
      setCostMetrics(null);
    } finally {
      setCostMetricsLoading(false);
    }
  };

  const loadCostCampaignSources = async () => {
    try {
      const [sources, progress] = await Promise.all([
        drillingService.fetchProjectSources(),
        drillingService.fetchSampleProgressDashboard(),
      ]);
      setProjectSources(sources || []);
      setSampleProgressProjects(progress?.projects || []);
    } catch (err) {
      console.warn('Campaign sources for lab cost metrics unavailable:', err);
    }
  };

  const costCampaignTabLabel = useCallback(
    (src) => {
      const pid = src?.project_id;
      const row = (sampleProgressProjects || []).find(
        (p) =>
          p.source_project_id != null &&
          String(p.source_project_id) === String(pid)
      );
      if (row?.source_project_name) return String(row.source_project_name);
      const fromMetrics = (costMetrics?.projects || []).find(
        (p) => String(p.project_id) === String(pid)
      );
      return fromMetrics?.project_name || String(pid || 'Project');
    },
    [sampleProgressProjects, costMetrics]
  );

  const costCampaignTabList = useMemo(() => {
    const ids = new Set();
    (projectSources || []).forEach((s) => {
      if (s.project_id != null && String(s.project_id) !== '') {
        ids.add(String(s.project_id));
      }
    });
    (costMetrics?.projects || []).forEach((p) => {
      if (p.project_id != null) ids.add(String(p.project_id));
    });
    return [...ids]
      .sort((a, b) =>
        costCampaignTabLabel({ project_id: a }).localeCompare(
          costCampaignTabLabel({ project_id: b })
        )
      )
      .map((project_id) => ({ project_id }));
  }, [projectSources, costMetrics, costCampaignTabLabel]);

  const selectedCostBlock = useMemo(() => {
    if (!costMetrics) return null;
    if (selectedProjectId === 'all') {
      return costMetrics.all_projects || null;
    }
    return (
      (costMetrics.projects || []).find(
        (p) => String(p.project_id) === String(selectedProjectId)
      ) || null
    );
  }, [costMetrics, selectedProjectId]);

  const selectedCostTotals = useMemo(() => {
    const byType = selectedCostBlock?.by_type || {};
    let workorders = 0;
    let samples = 0;
    WORKORDER_TYPE_DISPLAY_ORDER.forEach((typeKey) => {
      const bucket = byType[typeKey];
      if (!bucket) return;
      workorders += bucket.workorder_count || 0;
      samples += bucket.analysis_samples || 0;
    });
    return { workorders, samples };
  }, [selectedCostBlock]);

  const loadLastSync = async () => {
    try {
      setLastSyncLoading(true);
      console.log('🔄 Loading last sync timestamp...');
      const sync = await sampleTrackingService.getLastSyncTimestamp();
      console.log('✅ Last sync data:', sync);
      setLastSync(sync);
    } catch (err) {
      console.error('⚠️ Failed to load last sync timestamp:', err);
      setLastSync(null);
      // Don't set error state - this is non-critical
    } finally {
      setLastSyncLoading(false);
    }
  };

  const loadSyncHistory = async () => {
    try {
      console.log('📊 Loading sync history...');
      const result = await sampleTrackingService.getAlsSyncLog({ limit: 50 });
      console.log('✅ Sync history loaded:', result);
      setSyncHistory(result.data || []);
      setShowSyncModal(true);
      console.log('📋 Sync history records:', result.data?.length || 0);
    } catch (err) {
      console.error('❌ Error loading sync history:', err);
      alert('Failed to load sync history. Please try again.');
    }
  };

  const loadSampleLocationData = async () => {
    try {
      console.log('📍 Loading sample location data...');
      const data = await sampleTrackingService.getSampleLocation();
      console.log('✅ Sample location data loaded:', data?.length || 0, 'records');
      
      // Log location breakdown for debugging
      if (data && data.length > 0) {
        const locationCounts = {};
        data.forEach(item => {
          const loc = item.location || 'Unknown';
          locationCounts[loc] = (locationCounts[loc] || 0) + 1;
        });
        console.log('📊 Location breakdown:', locationCounts);
        
        // Show sample data
        console.log('📋 Sample record:', data[0]);
      }
      
      setSampleLocationData(data || []);
    } catch (err) {
      console.error('⚠️ Failed to load sample location (non-critical):', err);
      // Don't set error - this is non-critical for the main dashboard
    }
  };

  const handleLocationClick = (locationName, dispatches) => {
    console.log('📍 Location clicked:', locationName, dispatches);
    setSelectedDispatch({ location: locationName, dispatches });
    setShowDispatchModal(true);
  };

  // Filter and sort data
  const filteredData = React.useMemo(() => {
    let filtered = [...trackingData].filter(
      (hole) => (hole.workorder_numbers_array?.length || 0) > 0
    );

    // Apply search filter (hole_id OR workorder number)
    filtered = sampleTrackingService.filterData(filtered, searchTerm);
    
    // Apply status filter
    filtered = sampleTrackingService.filterByStatus(filtered, statusFilter);
    
    // Apply QAQC filter
    filtered = sampleTrackingService.filterByQaqc(filtered, qaqcFilter);
    
    // Apply news release filter
    filtered = sampleTrackingService.filterByNewsRelease(filtered, newsReleaseFilter);
    
    // Apply sorting
    filtered = sampleTrackingService.sortData(filtered, sortField, sortDirection);
    
    return filtered;
  }, [trackingData, searchTerm, statusFilter, qaqcFilter, newsReleaseFilter, sortField, sortDirection]);

  const handleExportCsv = useCallback(() => {
    if (!filteredData.length) return;

    const rows = filteredData.map((hole) => {
      const out = {};
      SAMPLE_TRACKING_CSV_COLUMNS.forEach(({ header, get }) => {
        out[header] = serializeSampleTrackingField(get(hole));
      });
      return out;
    });

    const datestamp = new Date().toISOString().split('T')[0];
    exportToCSV(`sample-tracking-${datestamp}.csv`, rows);
  }, [filteredData]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleRowClick = (hole) => {
    setSelectedHole(hole);
  };

  const handleCloseDetail = () => {
    setSelectedHole(null);
  };

  // Format date string without timezone conversion (dates are stored in PST, show as-is)
  const formatNewsReleaseDate = (dateString) => {
    if (!dateString || dateString === '' || dateString === 'null') return null;
    
    // Date is stored as YYYY-MM-DD in PST, format it without timezone conversion
    const parts = dateString.split('-');
    if (parts.length !== 3) return dateString; // Return as-is if not in expected format
    
    const year = parts[0];
    const month = parts[1];
    const day = parts[2];
    
    // Convert month number to short name
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthIndex = parseInt(month, 10) - 1;
    const monthName = monthNames[monthIndex] || month;
    
    return `${monthName} ${parseInt(day, 10)}, ${year}`;
  };

  // Get distinct news release dates from tracking data
  const distinctNewsReleaseDates = React.useMemo(() => {
    if (!trackingData || trackingData.length === 0) return [];
    
    const dates = new Set();
    trackingData.forEach(hole => {
      if (hole.news_release_date) {
        // Parse the date and format it consistently
        const dateStr = hole.news_release_date;
        if (dateStr && dateStr !== 'null' && dateStr !== '') {
          dates.add(dateStr);
        }
      }
    });
    
    // Convert to array and sort
    return Array.from(dates).sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateB - dateA; // Most recent first
    });
  }, [trackingData]);

  // Create a map of news release dates to color indices for alternating colors
  const newsReleaseDateColorMap = React.useMemo(() => {
    const map = new Map();
    distinctNewsReleaseDates.forEach((date, index) => {
      map.set(date, index % 2); // Alternate between 0 and 1
    });
    return map;
  }, [distinctNewsReleaseDates]);

  // Get row highlight class based on news release date
  const getRowHighlightClass = (hole) => {
    if (!hole.news_release_date || hole.news_release_date === '' || hole.news_release_date === 'null') {
      return ''; // No highlight for unreleased
    }
    const colorIndex = newsReleaseDateColorMap.get(hole.news_release_date);
    return colorIndex === 0 ? 'released-row-1' : 'released-row-2';
  };

  // Generate timeline data for next 30 days
  const getTimelineData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const thirtyDaysOut = new Date(today);
    thirtyDaysOut.setDate(thirtyDaysOut.getDate() + 30);
    
    // Group holes by their earliest ETA date
    const dateMap = new Map();
    
    trackingData.forEach(hole => {
      if (hole.eta_parsed && hole.eta_parsed.length > 0) {
        // Find earliest valid date
        const validDates = hole.eta_parsed.filter(eta => eta.date !== null && eta.date >= today && eta.date <= thirtyDaysOut);
        if (validDates.length > 0) {
          const earliest = validDates.sort((a, b) => a.sortable - b.sortable)[0];
          const dateKey = earliest.date.toDateString();
          
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, {
              date: earliest.date,
              holes: [],
              count: 0
            });
          }
          
          dateMap.get(dateKey).holes.push(hole);
          dateMap.get(dateKey).count += 1;
        }
      }
    });
    
    return Array.from(dateMap.values()).sort((a, b) => a.date - b.date);
  };

  const timelineData = React.useMemo(() => getTimelineData(), [trackingData]);

  const formatEtaDisplay = (etaParsed) => {
    if (!etaParsed || etaParsed.length === 0) return 'N/A';
    
    // Get unique display values
    const unique = [...new Set(etaParsed.map(e => e.display))];
    return unique.join(', ');
  };

  const getStatusBadgeClass = (statuses) => {
    if (!statuses || statuses.length === 0) return 'status-unknown';
    
    const hasActive = statuses.some(s => s === 'Active');
    const hasHold = statuses.some(s => s === 'Hold');
    const allFinalized = statuses.every(s => s === 'Finalized');
    
    if (allFinalized) return 'status-finalized';
    if (hasHold) return 'status-hold';
    if (hasActive) return 'status-active';
    return 'status-unknown';
  };

  const getHoleQaqcArray = (hole) => sampleTrackingService.getQaqcArrayForHole(hole);

  const colmatchStatusTooltip = (hole) => {
    const status = hole.colmatch_display_status;
    const parts = [
      sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatch),
      sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatchFailedStatus),
    ];
    if (status?.tooltip) parts.push(status.tooltip);
    return parts.filter(Boolean).join(' — ');
  };

  const renderColmatchStatusBadge = (hole) => {
    const status = hole.colmatch_display_status;
    if (!status?.label || status.label === 'N/A') {
      if (!hole.colmatch_has_data && hole.colmatch_hole_passed == null) {
        return 'N/A';
      }
    }

    const label = status?.label || 'N/A';
    const badgeClass = status?.badgeClass || '';
    const badge = (
      <span className={`colmatch-badge ${badgeClass}`.trim()}>{label}</span>
    );

    if (
      status?.code === COLMATCH_STATUS.FAILED &&
      status.failureLink &&
      canAccessQaqc
    ) {
      const sample = status.failureLink.sampleNumber;
      return (
        <button
          type="button"
          className="colmatch-failure-link source-help"
          title={colmatchStatusTooltip(hole)}
          onClick={(e) => {
            e.stopPropagation();
            navigateToQaqcFailure(status.failureLink);
          }}
        >
          {badge}
          {sample ? (
            <span className="colmatch-failure-sample">Sample {sample}</span>
          ) : null}
        </button>
      );
    }

    return (
      <span className="source-help" title={colmatchStatusTooltip(hole)}>
        {badge}
      </span>
    );
  };

  const formatColmatchSampleCounts = (hole) => {
    if (
      hole.colmatch_passed_sample_count == null &&
      hole.colmatch_expected_sample_count == null
    ) {
      return 'N/A';
    }
    const passed = hole.colmatch_passed_sample_count ?? '—';
    const expected = hole.colmatch_expected_sample_count ?? '—';
    return `${passed}/${expected}`;
  };

  const getQaqcCellTitle = (hole, qaqcArray) => {
    const parts = [sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.qaqcBadge)];
    const allWoPassed =
      qaqcArray?.length > 0 && qaqcArray.every((q) => q === 't');
    if (allWoPassed && hole.colmatch_hole_passed === false) {
      parts.push('Workorders passed; hole colmatch failed');
      if (hole.colmatch_fail_reason) {
        parts.push(hole.colmatch_fail_reason);
      }
      if (hole.colmatch_missing_passed_sample_count != null) {
        parts.push(`Missing passed samples: ${hole.colmatch_missing_passed_sample_count}`);
      }
    }
    return parts.join(' — ');
  };

  const renderWorkorderDetailCard = (wo, key) => {
    const showQaqc = isQaqcRelevantWorkorderType(wo.workorder_type);
    return (
      <div key={key} className="workorder-detail-card">
        <div className="wo-header">
          <span
            className="wo-number source-help"
            title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderList)}
          >
            {wo.workorder_id}
            {wo.cpt && (
              <span
                className="wo-cpt source-help"
                title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderCpt)}
              >
                {wo.cpt}
              </span>
            )}
          </span>
          <span
            className={`wo-status ${wo.status?.toLowerCase()} source-help`}
            title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderStatus)}
          >
            {wo.status || 'Unknown'}
          </span>
        </div>
        <div className="wo-body">
          {wo.depth_range && (
            <div className="wo-info-item">
              <span
                className="wo-info-label source-help"
                title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderDepth)}
              >
                <IconText icon={ICON.uiDepth} iconSize={14} as="span">Depth Range:</IconText>
              </span>
              <span className="wo-info-value">{wo.depth_range}m</span>
            </div>
          )}
          {wo.sample_count !== undefined && wo.sample_count !== null && (
            <div className="wo-info-item">
              <span
                className="wo-info-label source-help"
                title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderSampleCount)}
              >
                <IconText icon={ICON.uiSequentialLeach} iconSize={14} as="span">Samples:</IconText>
              </span>
              <span className="wo-info-value">{wo.sample_count}</span>
            </div>
          )}
          {wo.final_eta_display && (
            <div className="wo-info-item">
              <span
                className="wo-info-label source-help"
                title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.finalEta)}
              >
                <IconText icon={ICON.uiClockEta} iconSize={14} as="span">Final ETA:</IconText>
              </span>
              <span className="wo-info-value">{wo.final_eta_display}</span>
            </div>
          )}
          {showQaqc && wo.qaqc_passed !== undefined && wo.qaqc_passed !== null && (
            <div className="wo-info-item">
              <span
                className="wo-info-label source-help"
                title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderQaqc)}
              >
                <IconText icon={ICON.uiCheckOn} iconSize={14} as="span">QAQC:</IconText>
              </span>
              <span className={`wo-info-value ${
                wo.qaqc_passed ? 'passed' : (wo.status === 'Active' ? 'pending' : 'failed')
              }`}>
                {wo.qaqc_passed ? 'Passed' : (wo.status === 'Active' ? 'Pending' : 'Failed')}
              </span>
            </div>
          )}
          {showQaqc && wo.has_unresolved_failure !== undefined && wo.has_unresolved_failure !== null && wo.has_unresolved_failure && (
            <div className="wo-info-item">
              <span className="wo-info-label"><IconText icon={ICON.uiWarning} iconSize={14} as="span">Status:</IconText></span>
              <span className="wo-info-value warning">Unresolved Failure</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const formatSyncTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    
    try {
      const date = new Date(timestamp);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Unknown';
    }
  };

  return (
    <Layout user={user} currentRoute="/sample-tracking">
      <div className="sample-tracking">
        <div className="sample-tracking-header">
          <div className="header-left">
            <IconText icon={ICON.appSampleTracking} as="h1">Sample Tracking Dashboard</IconText>
            <p>Monitor workorders, QAQC results, and news release progress</p>
          </div>
          <div className="header-right">
            <div 
              className="last-updated source-help" 
              onClick={loadSyncHistory} 
              title={lastSync
                ? `${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.alsSyncLog)} — Click to view sync history`
                : `${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.alsSyncLog)} — Sync history unavailable`}
            >
              <span className="last-updated-label">Last Updated:</span>
              <span className="last-updated-time">
                {lastSyncLoading 
                  ? 'Checking...'
                  : lastSync 
                    ? formatSyncTimestamp(lastSync.end_time || lastSync.start_time)
                    : 'Unavailable'}
              </span>
              <SelkirkIcon name={ICON.uiChartAnalytics} size={18} decorative className="last-updated-icon" />
            </div>
          </div>
        </div>

        {loading && (
          <div className="loading-container">
            <Spinner size="large" />
            <p>Loading sample tracking data...</p>
          </div>
        )}

        {error && (
          <Card className="error-card">
            <IconText icon={ICON.uiError} as="h3">Error Loading Data</IconText>
            <p>{error}</p>
            <Button onClick={loadDashboardData}>Retry</Button>
          </Card>
        )}

        {!loading && !error && (
          <>
            {/* Warning Card - Partial Data */}
            {warnings && (warnings.collarsFailed || warnings.resultsFailed || warnings.workordersFailed || warnings.colmatchFailed) && (
              <Card className="warning-card">
                <IconText icon={ICON.uiWarning} as="h3">Partial Data Loaded</IconText>
                <p>Some data sources were unavailable. The dashboard is showing partial information:</p>
                <ul>
                  {warnings.collarsFailed && <li><IconText icon={ICON.uiError} iconSize={14} as="span">Collar locations unavailable</IconText></li>}
                  {warnings.resultsFailed && <li><IconText icon={ICON.uiError} iconSize={14} as="span">Result progress unavailable</IconText></li>}
                  {warnings.workordersFailed && <li><IconText icon={ICON.uiError} iconSize={14} as="span">Workorder progress unavailable (check permissions)</IconText></li>}
                  {warnings.colmatchFailed && <li><IconText icon={ICON.uiError} iconSize={14} as="span">Hole colmatch (sample counts) unavailable</IconText></li>}
                  {warnings.unresolvedFailuresFailed && <li><IconText icon={ICON.uiError} iconSize={14} as="span">Unresolved QAQC failures feed unavailable (Failed status may be incomplete)</IconText></li>}
                </ul>
                <p className="hint">Check Azure Function logs for details or contact your administrator.</p>
              </Card>
            )}

            {/* Summary Metrics Card */}
            {summaryMetrics && (
              <Card className="summary-metrics-card">
                <IconText icon={ICON.uiChartAnalytics} as="h2">Overall Progress Summary</IconText>
                <div className="metrics-grid">
                  <div
                    className="metric source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.summaryHoles)}
                  >
                    <span className="metric-label">Total Drill Holes</span>
                    <span className="metric-value">{summaryMetrics.total_holes?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div
                    className="metric source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.summaryWorkorders)}
                  >
                    <span className="metric-label">Total Workorders</span>
                    <span className="metric-value">{summaryMetrics.total_workorders?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div
                    className="metric source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderStatus)}
                  >
                    <span className="metric-label">Active Workorders</span>
                    <span className="metric-value active">{summaryMetrics.active_workorders?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div
                    className="metric source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderStatus)}
                  >
                    <span className="metric-label">Finalized Workorders</span>
                    <span className="metric-value finalized">{summaryMetrics.finalized_workorders?.toLocaleString() || 'N/A'}</span>
                  </div>
                  <div
                    className="metric source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.summaryQaqcPassed)}
                  >
                    <span className="metric-label">Holes with QAQC Passed</span>
                    <span className="metric-value passed">{summaryMetrics.holes_with_qaqc_passed?.toLocaleString() || 'N/A'}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Lab approval cost estimates (campaign-scoped) */}
            <Card className="summary-metrics-card lab-cost-metrics-card">
              <IconText icon={ICON.uiChartAnalytics} as="h2">
                Lab approval cost estimates (approved workorders)
              </IconText>
              <p
                className="lab-cost-metrics-card__intro source-help"
                title={`${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.labApprovalCost)} — ${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.labApprovalCostFormula)}`}
              >
                Weighted average cost per analysis sample from approved lab approvals, grouped by
                workorder type. Campaign tabs apply to this section only.
              </p>

              {costCampaignTabList.length > 0 && (
                <div
                  className="sample-tracking-campaign-tab-bar"
                  role="tablist"
                  aria-label="Drilling campaign for lab cost metrics"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={selectedProjectId === 'all'}
                    className={`sample-tracking-campaign-tab-btn${
                      selectedProjectId === 'all' ? ' sample-tracking-campaign-tab-btn--active' : ''
                    }`}
                    onClick={() => setSelectedProjectId('all')}
                  >
                    All
                  </button>
                  {costCampaignTabList.map((src) => (
                    <button
                      key={String(src.project_id)}
                      type="button"
                      role="tab"
                      aria-selected={selectedProjectId === String(src.project_id)}
                      className={`sample-tracking-campaign-tab-btn${
                        selectedProjectId === String(src.project_id)
                          ? ' sample-tracking-campaign-tab-btn--active'
                          : ''
                      }`}
                      onClick={() => setSelectedProjectId(String(src.project_id))}
                    >
                      {costCampaignTabLabel(src)}
                    </button>
                  ))}
                </div>
              )}

              {costMetricsLoading && (
                <p className="lab-cost-metrics-card__status">Loading lab cost metrics…</p>
              )}
              {costMetricsError && !costMetricsLoading && (
                <p className="lab-cost-metrics-card__error" role="alert">
                  {costMetricsError}
                </p>
              )}
              {!costMetricsLoading && !costMetricsError && costMetrics && (
                <>
                  <div className="metrics-grid lab-cost-metrics-grid">
                    {WORKORDER_TYPE_DISPLAY_ORDER.map((typeKey) => {
                      const bucket = selectedCostBlock?.by_type?.[typeKey];
                      const tooltipParts = [
                        sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.labApprovalCost),
                        sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.labApprovalCostFormula),
                      ];
                      if (bucket) {
                        tooltipParts.push(
                          `${bucket.workorder_count} workorder(s), ${bucket.analysis_samples} analysis sample(s)`
                        );
                      }
                      return (
                        <div
                          key={typeKey}
                          className="metric source-help lab-cost-metric"
                          title={tooltipParts.join(' — ')}
                        >
                          <span className="metric-label">
                            {WORKORDER_TYPE_LABELS[typeKey] || typeKey}
                          </span>
                          <span className="metric-value lab-cost-metric__value">
                            {formatCadCurrency(bucket?.avg_cost_per_sample)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="lab-cost-metrics-card__footnote">
                    {selectedProjectId === 'all'
                      ? 'All campaigns'
                      : costCampaignTabLabel({ project_id: selectedProjectId })}
                    {' — '}
                    Based on {selectedCostTotals.workorders.toLocaleString()} approved workorder
                    {selectedCostTotals.workorders === 1 ? '' : 's'}
                    {', '}
                    {selectedCostTotals.samples.toLocaleString()} analysis sample
                    {selectedCostTotals.samples === 1 ? '' : 's'} in these type buckets.
                  </p>
                </>
              )}
            </Card>

            {/* ETA Timeline & Sample Location Map - Tabbed View */}
            <Card className="timeline-map-card">
              <div className="tab-header">
                <div className="tabs">
                  <button 
                    className={`tab ${activeTab === 'timeline' ? 'active' : ''}`}
                    onClick={() => setActiveTab('timeline')}
                  >
                    <IconText icon={ICON.uiCalendarDate} iconSize={14}>Timeline</IconText>
                  </button>
                  <button 
                    className={`tab ${activeTab === 'map' ? 'active' : ''}`}
                    onClick={() => setActiveTab('map')}
                  >
                    <IconText icon={ICON.uiLocationPin} iconSize={14}>Location Map</IconText>
                  </button>
                </div>
              </div>

              {/* Timeline Tab */}
              {activeTab === 'timeline' && (
                <>
                  <h2
                    className="source-help"
                    style={{ marginTop: '1rem' }}
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.timeline)}
                  >
                    <IconText icon={ICON.uiCalendarDate} iconSize={14}>Upcoming Sample Completions - Next 30 Days</IconText>
                  </h2>
                  {timelineData.length > 0 ? (
                    <div className="timeline-container">
                      {timelineData.map((item, index) => {
                        const dateStr = item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                        const dayOfWeek = item.date.toLocaleDateString('en-US', { weekday: 'short' });
                        const isToday = item.date.toDateString() === new Date().toDateString();
                        
                        return (
                          <div 
                            key={index} 
                            className={`timeline-item ${isToday ? 'today' : ''}`}
                            title={`${item.count} hole${item.count > 1 ? 's' : ''} - ${item.holes.map(h => h.hole_id).join(', ')}`}
                          >
                            <div className="timeline-date">
                              <span className="timeline-day">{dayOfWeek}</span>
                              <span className="timeline-date-label">{dateStr}</span>
                            </div>
                            <div className="timeline-marker">
                              <div className="timeline-dot" style={{
                                width: `${Math.min(20 + (item.count * 4), 60)}px`,
                                height: `${Math.min(20 + (item.count * 4), 60)}px`
                              }}>
                                <span className="timeline-count">{item.count}</span>
                              </div>
                            </div>
                            <div className="timeline-holes">
                              {item.holes.slice(0, 3).map((hole, idx) => (
                                <span key={idx} className="timeline-hole-id">{hole.hole_id}</span>
                              ))}
                              {item.count > 3 && <span className="timeline-more">+{item.count - 3}</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="timeline-empty">No upcoming ETAs in the next 30 days</p>
                  )}
                </>
              )}

              {/* Location Map Tab */}
              {activeTab === 'map' && (
                <>
                  <h2
                    className="source-help"
                    style={{ marginTop: '1rem' }}
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}
                  >
                    <IconText icon={ICON.uiLocationPin} iconSize={14}>Sample Location Pipeline</IconText>
                  </h2>
                  {sampleLocationData.length > 0 ? (
                    <div style={{ marginTop: '1rem' }}>
                      <SampleLocationMap 
                        data={sampleLocationData} 
                        onLocationClick={handleLocationClick}
                      />
                    </div>
                  ) : (
                    <p className="timeline-empty">Loading sample location data...</p>
                  )}
                </>
              )}
            </Card>

            {/* Search and Filter Controls */}
            <Card className="controls-card">
              <div className="controls-grid">
                <div className="search-box">
                  <input
                    type="text"
                    placeholder="Search by Hole ID or Workorder Number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="controls-filters">
                  <div className="filter-group">
                    <label>Status Filter:</label>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                      <option value="all">All Statuses</option>
                      <option value="Active">Active</option>
                      <option value="Finalized">Finalized</option>
                      <option value="Hold">Hold</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>QAQC Filter:</label>
                    <select value={qaqcFilter} onChange={(e) => setQaqcFilter(e.target.value)}>
                      <option value="all">All</option>
                      <option value="passed">Passed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>News Release:</label>
                    <select value={newsReleaseFilter} onChange={(e) => setNewsReleaseFilter(e.target.value)}>
                      <option value="all">All Holes</option>
                      <option value="not_released">Not Released</option>
                      <option value="released">All Released</option>
                      <optgroup label="By Release Date">
                        {distinctNewsReleaseDates.map(date => (
                          <option key={date} value={date}>
                            {formatNewsReleaseDate(date)}
                          </option>
                        ))}
                      </optgroup>
                    </select>
                  </div>
                </div>

                <div className="controls-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleExportCsv}
                    disabled={!filteredData.length}
                  >
                    Download CSV
                  </Button>

                  <Button onClick={syncAndLoadData} variant="secondary" size="sm">
                    <IconText icon={ICON.uiRefresh} iconSize={14}>Refresh</IconText>
                  </Button>
                </div>
              </div>
            </Card>

            {/* Sample Tracking Table */}
            <Card className="tracking-table-card">
              <IconText icon={ICON.appSampleTracking} as="h2">Workorder & QAQC Status</IconText>
              <p className="table-subtitle">
                Showing {filteredData.length} of {trackingData.length} drill holes
              </p>
              
              {filteredData.length === 0 ? (
                <div className="no-data">
                  <p>No data matches your current filters</p>
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th
                          onClick={() => handleSort('hole_id')}
                          className="sortable source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.holeList)}
                        >
                          Hole ID {sortIndicator('hole_id')}
                        </th>
                        <th
                          onClick={() => handleSort('expected_completion_date')}
                          className="sortable source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.expectedCompletion)}
                        >
                          Expected Completion {sortIndicator('expected_completion_date')}
                        </th>
                        <th
                          className="source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderCount)}
                        >
                          Workorder Count
                        </th>
                        <th
                          className="source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderStatus)}
                        >
                          Status
                        </th>
                        <th
                          className="source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.qaqcBadge)}
                        >
                          QAQC
                        </th>
                        <th
                          onClick={() => handleSort('colmatch_passed_sample_count')}
                          className="sortable source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatch)}
                        >
                          Samples passed {sortIndicator('colmatch_passed_sample_count')}
                        </th>
                        <th
                          onClick={() => handleSort('colmatch_display_label')}
                          className="sortable source-help"
                          title={`${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatch)}; ${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatchFailedStatus)}`}
                        >
                          Colmatch {sortIndicator('colmatch_display_label')}
                        </th>
                        <th
                          onClick={() => handleSort('news_release_date')}
                          className="sortable source-help"
                          title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.newsRelease)}
                        >
                          News Release {sortIndicator('news_release_date')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((hole, index) => (
                        <tr 
                          key={index} 
                          onClick={() => handleRowClick(hole)}
                          className={`clickable-row ${getRowHighlightClass(hole)}`}
                        >
                          <td className="hole-id">
                            {hole.priority && (
                              <span
                                className={`priority-star priority-${hole.priority}`}
                                title={`${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.priority)} — Priority ${hole.priority}`}
                              >
                                <SelkirkIcon name={ICON.uiStar} size={14} decorative />
                              </span>
                            )}
                            {hole.hole_id}
                          </td>
                          <td className="eta-cell">
                            {hole.expected_completion_date 
                              ? formatNewsReleaseDate(hole.expected_completion_date) 
                              : 'TBD'}
                          </td>
                          <td className="text-center">
                            {hole.workorder_numbers_array?.length || 0}
                          </td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(hole.workorder_statuses_array)}`}>
                              {hole.workorder_statuses_array?.length > 0 
                                ? `${hole.workorder_statuses_array.filter(s => s === 'Active').length}A / ${hole.workorder_statuses_array.filter(s => s === 'Finalized').length}F`
                                : 'N/A'}
                            </span>
                          </td>
                          <td>
                            {(() => {
                              const qaqcArray = getHoleQaqcArray(hole);
                              return (
                            <span
                              className={`qaqc-badge source-help ${sampleTrackingService.getQaqcBadgeClassForHole(hole, qaqcArray)}`}
                              title={getQaqcCellTitle(hole, qaqcArray)}
                            >
                              {qaqcArray?.length > 0
                                ? `${qaqcArray.filter(q => q === 't').length}/${qaqcArray.length}`
                                : 'N/A'}
                            </span>
                              );
                            })()}
                          </td>
                          <td className="text-center">
                            <span
                              className="source-help"
                              title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatch)}
                            >
                              {formatColmatchSampleCounts(hole)}
                            </span>
                          </td>
                          <td className="colmatch-cell">{renderColmatchStatusBadge(hole)}</td>
                          <td className="news-release-cell">
                            {hole.news_release_date && hole.news_release_date !== '' && hole.news_release_date !== 'null' ? (
                              <span className="news-release-badge released">
                                {formatNewsReleaseDate(hole.news_release_date)}
                              </span>
                            ) : (
                              <span className="news-release-badge not-released">Not Released</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}

        {/* Drill Hole Detail Modal */}
        {selectedHole && (
          <div className="modal-overlay" onClick={handleCloseDetail}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <Card className="hole-detail-card">
                <div className="modal-header">
                  <IconText icon={ICON.uiClipboardList} as="h2">Drill Hole Details: {selectedHole.hole_id}</IconText>
                  <button className="close-button" onClick={handleCloseDetail}>×</button>
                </div>
                
                {/* Collar Location Section */}
                <div className="detail-section">
                  <h3
                    className="source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.collarLocation)}
                  >
                    <IconText icon={ICON.uiLocationPin} iconSize={14}>Collar Location</IconText>
                  </h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Easting:</span>
                      <span className="detail-value">{selectedHole.easting || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Northing:</span>
                      <span className="detail-value">{selectedHole.northing || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Azimuth:</span>
                      <span className="detail-value">{selectedHole.azi || 'N/A'}°</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Dip:</span>
                      <span className="detail-value">{selectedHole.dip || 'N/A'}°</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">End of Hole:</span>
                      <span className="detail-value">{selectedHole.eoh || 'N/A'}m</span>
                    </div>
                  </div>
                </div>

                {/* Expected Completion & ETA Information */}
                <div className="detail-section">
                  <h3
                    className="source-help"
                    title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.expectedCompletion)}
                  >
                    <IconText icon={ICON.uiCalendarDate} iconSize={14}>Expected Completion</IconText>
                  </h3>
                  <div className="eta-list">
                    <span className="eta-item">
                      {selectedHole.expected_completion_date 
                        ? formatNewsReleaseDate(selectedHole.expected_completion_date) 
                        : 'TBD'}
                    </span>
                  </div>
                </div>

                {/* Workorder Details */}
                <div className="detail-section">
                  <h3
                    className="source-help"
                    title={`${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderList)}; ${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderType)}`}
                  >
                    <IconText icon={ICON.uiClipboardList} iconSize={14}>Workorder Information</IconText>
                  </h3>
                  {selectedHole.workorder_details && selectedHole.workorder_details.length > 0 ? (
                    <div className="workorder-list">
                      {groupWorkorderDetailsByType(selectedHole.workorder_details).map((group) => (
                        <div key={group.typeLabel} className="workorder-type-group">
                          <h4
                            className="workorder-type-group-title source-help"
                            title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.workorderType)}
                          >
                            {group.typeLabel}
                          </h4>
                          {group.workorders.map((wo) =>
                            renderWorkorderDetailCard(wo, `${group.typeLabel}-${wo.workorder_id}`)
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No workorder information</p>
                  )}
                </div>

                {selectedHole.colmatch_has_data ||
                selectedHole.colmatch_hole_passed === true ||
                selectedHole.colmatch_hole_passed === false ||
                selectedHole.colmatch_display_status ? (
                  <div className="detail-section">
                    <h3
                      className="source-help"
                      title={`${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatch)}; ${sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.colmatchFailedStatus)}`}
                    >
                      Colmatch (ORIG samples)
                    </h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Status:</span>
                        <span className="detail-value">{renderColmatchStatusBadge(selectedHole)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Samples passed / expected:</span>
                        <span className="detail-value">{formatColmatchSampleCounts(selectedHole)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Hole passed (view):</span>
                        <span className="detail-value">
                          {selectedHole.colmatch_hole_passed === true
                            ? 'Yes'
                            : selectedHole.colmatch_hole_passed === false
                              ? 'No'
                              : 'N/A'}
                        </span>
                      </div>
                      {selectedHole.colmatch_fail_reason && (
                        <div className="detail-item">
                          <span className="detail-label">Fail reason:</span>
                          <span className="detail-value">{selectedHole.colmatch_fail_reason}</span>
                        </div>
                      )}
                      {selectedHole.colmatch_missing_passed_sample_count != null && (
                        <div className="detail-item">
                          <span className="detail-label">Missing passed:</span>
                          <span className="detail-value">
                            {selectedHole.colmatch_missing_passed_sample_count}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* News Release Dates */}
                {selectedHole.news_release_dates_array && selectedHole.news_release_dates_array.length > 0 && (
                  <div className="detail-section">
                    <h3
                      className="source-help"
                      title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.newsReleaseOrdered)}
                    >
                      <IconText icon={ICON.uiNews} iconSize={14}>News Release Dates</IconText>
                    </h3>
                    <div className="news-release-list">
                      {[...new Set(selectedHole.news_release_dates_array)].map((date, idx) => (
                        <span key={idx} className="news-date">
                          {date}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="modal-footer">
                  <Button onClick={handleCloseDetail}>Close</Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Sync History Modal */}
        {showSyncModal && (
          <div className="modal-overlay" onClick={() => setShowSyncModal(false)}>
            <div className="modal-content sync-modal" onClick={(e) => e.stopPropagation()}>
              <Card className="sync-modal-card">
                <div className="modal-header">
                  <IconText icon={ICON.uiChartAnalytics} as="h2">ALS Sync History</IconText>
                  <button className="close-button" onClick={() => setShowSyncModal(false)}>×</button>
                </div>

                <div className="sync-history-container">
                  {syncHistory.length > 0 ? (
                    <table className="sync-history-table">
                      <thead>
                        <tr>
                          <th>Execution ID</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Start Time</th>
                          <th>End Time</th>
                          <th>Duration</th>
                          <th>WO Fetched</th>
                          <th>Inserted</th>
                          <th>Updated</th>
                          <th>Errors</th>
                          <th>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {syncHistory.map((sync, idx) => {
                          const duration = sync.duration_seconds 
                            ? `${sync.duration_seconds.toFixed(1)}s`
                            : (sync.start_time && sync.end_time 
                              ? `${((new Date(sync.end_time) - new Date(sync.start_time)) / 1000).toFixed(1)}s`
                              : 'N/A');
                          
                          return (
                            <tr key={idx} className={sync.status?.toLowerCase()}>
                              <td className="execution-id">{sync.execution_id?.substring(0, 12) || 'N/A'}</td>
                              <td>{sync.sync_type || 'N/A'}</td>
                              <td>
                                <span className={`status-badge ${sync.status?.toLowerCase()}`}>
                                  {sync.status || 'UNKNOWN'}
                                </span>
                              </td>
                              <td className="timestamp">
                                {sync.start_time 
                                  ? new Date(sync.start_time).toLocaleString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'N/A'}
                              </td>
                              <td className="timestamp">
                                {sync.end_time 
                                  ? new Date(sync.end_time).toLocaleString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit'
                                    })
                                  : 'N/A'}
                              </td>
                              <td>{duration}</td>
                              <td className="count">{sync.workorders_fetched?.toLocaleString() || 0}</td>
                              <td className="count success">{sync.total_records_inserted?.toLocaleString() || 0}</td>
                              <td className="count">{sync.total_records_updated?.toLocaleString() || 0}</td>
                              <td className="count error">{sync.error_count?.toLocaleString() || 0}</td>
                              <td className="error-message">
                                {sync.error_message ? (
                                  <span title={sync.error_message}>
                                    {sync.error_message.substring(0, 40)}
                                    {sync.error_message.length > 40 && '...'}
                                  </span>
                                ) : (sync.notes ? (
                                  <span title={sync.notes}>
                                    {sync.notes.substring(0, 40)}
                                    {sync.notes.length > 40 && '...'}
                                  </span>
                                ) : '-')}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-sync-data">No sync history available</p>
                  )}
                </div>

                <div className="modal-footer">
                  <Button onClick={() => setShowSyncModal(false)}>Close</Button>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Dispatch Details Modal */}
        {showDispatchModal && selectedDispatch && (
          <div className="modal-overlay" onClick={() => setShowDispatchModal(false)}>
            <div className="modal-content dispatch-modal" onClick={(e) => e.stopPropagation()}>
              <Card className="dispatch-modal-card">
                <div className="modal-header">
                  <IconText icon={ICON.uiLocationPin} as="h2">{selectedDispatch.location} - Sample Details</IconText>
                  <button className="close-button" onClick={() => setShowDispatchModal(false)}>×</button>
                </div>

                <div className="dispatch-details-container">
                  {selectedDispatch.dispatches && selectedDispatch.dispatches.length > 0 ? (
                    <>
                      <div className="dispatch-summary" style={{ 
                        marginBottom: '1rem', 
                        padding: '1rem', 
                        background: 'rgba(249, 190, 11, 0.1)', 
                        borderRadius: '8px',
                        border: '1px solid rgba(249, 190, 11, 0.3)'
                      }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '600', marginBottom: '0.25rem' }}>
                              Total Dispatches
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#f9be0b' }}>
                              {selectedDispatch.dispatches.length}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '600', marginBottom: '0.25rem' }}>
                              Total Samples
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#10b981' }}>
                              {selectedDispatch.dispatches.reduce((sum, d) => {
                                const count = parseInt(d.sample_count || d.dispatch_sample_count || 0);
                                return sum + (isNaN(count) ? 0 : count);
                              }, 0).toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '600', marginBottom: '0.25rem' }}>
                              Workorders
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#8b5cf6' }}>
                              {selectedDispatch.dispatches.filter(d => d.dispatch_has_workorder).length}
                            </div>
                          </div>
                          <div>
                            <div style={{ fontSize: '0.75rem', color: '#9ca3af', fontWeight: '600', marginBottom: '0.25rem' }}>
                              Total Meters
                            </div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#06b6d4' }}>
                              {(() => {
                                const total = selectedDispatch.dispatches.reduce((sum, d) => {
                                  const meters = parseFloat(d.dispatch_total_sampled_m || 0);
                                  return sum + (isNaN(meters) ? 0 : meters);
                                }, 0);
                                return total.toFixed(1);
                              })()}m
                            </div>
                          </div>
                        </div>
                      </div>

                      <table className="dispatch-table">
                        <thead>
                          <tr>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Dispatch</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Workorder</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Status</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Samples</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Meters</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>QAQC</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Failure</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Depths</th>
                            <th className="source-help" title={sourceTooltip(SAMPLE_TRACKING_DATA_SOURCES.sampleLocation)}>Prep Approved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedDispatch.dispatches.map((dispatch, idx) => (
                            <tr key={idx}>
                              <td style={{ fontWeight: '600' }}>{dispatch.dispatch}</td>
                              <td>
                                {dispatch.dispatch_has_workorder ? (
                                  <span style={{ color: '#10b981', fontWeight: '600' }}>{dispatch.workorder_id || 'N/A'}</span>
                                ) : (
                                  <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>No WO</span>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${dispatch.status?.toLowerCase()}`}>
                                  {dispatch.status || 'Unknown'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{dispatch.sample_count || dispatch.dispatch_sample_count || 0}</td>
                              <td style={{ textAlign: 'center' }}>
                                {dispatch.dispatch_total_sampled_m ? parseFloat(dispatch.dispatch_total_sampled_m).toFixed(1) : '0.0'}m
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {dispatch.qaqc_passed === null ? (
                                  <span style={{ color: '#6b7280' }}>—</span>
                                ) : dispatch.qaqc_passed ? (
                                  <SelkirkIcon name={ICON.uiCheckOn} size={16} decorative />
                                ) : (
                                  <SelkirkIcon name={ICON.uiError} size={16} decorative />
                                )}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                {dispatch.has_unresolved_failure ? (
                                  <SelkirkIcon name={ICON.uiWarning} size={16} decorative />
                                ) : (
                                  <span style={{ color: '#6b7280' }}>—</span>
                                )}
                              </td>
                              <td style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                                {dispatch.workorder_depths || '—'}
                              </td>
                              <td style={{ fontSize: '0.875rem', color: '#9ca3af' }}>
                                {dispatch.prep_approved_date 
                                  ? new Date(dispatch.prep_approved_date).toLocaleDateString('en-US', { 
                                      month: 'short', 
                                      day: 'numeric',
                                      year: 'numeric'
                                    })
                                  : '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                      No dispatch details available
                    </p>
                  )}
                </div>

                <div className="modal-footer">
                  <Button onClick={() => setShowDispatchModal(false)}>Close</Button>
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SampleTracking;

